import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

import HandIcon from '../Icons/HandPointUp';

export default function FloatingButton({
  alternative,
  onClick,
  onFocus,
  position,
  size,
  avatarEnabled,
  img,
  title,
  textarea,
  textFontFamily,
  ...props
}) {
  const { t } = useTranslation();
  const handleClick = (e, ...args) => {
    if (onClick) {
      onClick(e, ...args);
    }
  };

  const handleFocus = (e, ...args) => {
    if (onFocus) {
      onFocus(e, ...args);
    }
  };

  return (
    <Root
      position={position}
      size={size}
      alternative={alternative}
      textFontFamily={textFontFamily}
      {...props}
    >
      {alternative ? (
        <Title onClick={handleClick} paddingRight={img.width} {...title}>
          <Subtitle>
            {t('clickHere')} <HandIcon fill={title.color} />
          </Subtitle>
          {title.content}
        </Title>
      ) : (
        <div>
          <Title paddingRight={img.width} {...title}>
            {title.content}
          </Title>
          <Textarea paddingRight={img.width} onFocus={handleFocus} {...textarea} />
        </div>
      )}
      {avatarEnabled && <Avatar {...img} />}
    </Root>
  );
}

FloatingButton.propTypes = {
  onClick: PropTypes.func,
  onFocus: PropTypes.func,
  position: PropTypes.shape({
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired
  }).isRequired,
  size: PropTypes.shape({
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired
  }).isRequired,
  alternative: PropTypes.bool,
  avatarEnabled: PropTypes.bool,
  img: PropTypes.shape({
    url: PropTypes.string.isRequired,
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired
  }).isRequired,
  textarea: PropTypes.shape({
    rows: PropTypes.number.isRequired,
    placeholder: PropTypes.string.isRequired,
    background: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired
  }),
  title: PropTypes.shape({
    content: PropTypes.string.isRequired,
    background: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired
  }),
  textFontFamily: PropTypes.string
};

const Title = styled.h3`
  padding-top: 6px;
  padding-right: ${({ paddingRight }) => `${paddingRight}px`};
  padding-bottom: 6px;
  padding-left: 10px;
  margin: 0 0 -1px;
  font-family: inherit;
  font-size: 14px !important;
  font-weight: 700;
  line-height: 18px;
  background: ${({ background }) => background};
  color: ${({ color }) => `${color}`};
`;

const Subtitle = styled.span`
  display: block;
  margin-bottom: 2px;
  font-family: inherit;
  font-size: 10px;
  font-weight: 400;
  line-height: 12px;
  color: inherit;

  svg {
    width: 12px;
    height: 12px;
  }
`;

const Textarea = styled.textarea`
  position: static;
  display: block;
  width: 100%;
  min-width: auto;
  height: 36px;
  min-height: auto;
  padding-top: 6px;
  padding-right: ${({ paddingRight }) => `${paddingRight}px`};
  padding-bottom: 6px;
  padding-left: 10px;
  margin: 0;
  font-family: inherit;
  font-size: 10px;
  font-weight: 400;
  line-height: 12px;
  color: ${({ color }) => `${color}`};
  background: ${({ background }) => background};
  border: none;
  box-shadow: none;
  resize: none;

  &::placeholder {
    color: ${({ color }) => `${color}`};
  }

  &:focus {
    outline: none !important;
  }
`;

const Avatar = styled.div.attrs(({ width, height, url }) => ({
  style: {
    width,
    height,
    backgroundImage: `url("${url}")`
  }
}))`
  position: absolute;
  right: 0;
  bottom: 0;
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
`;

const Root = styled.div.attrs(({ position: { y: top, x: left }, size: { width, height } }) => ({
  style: {
    top,
    left,
    width,
    height
  }
}))`
  position: fixed;
  z-index: 100000;
  font-family: ${({ textFontFamily }) => textFontFamily};
  box-shadow: 0 1px 5px 0 rgba(0, 0, 0, 0.3);
  box-sizing: border-box;

  ${({ alternative }) =>
    alternative &&
    css`
      ${Title} {
        border-radius: 3px;
      }

      ${Avatar} {
        top: 0%;
        bottom: unset;
        transform: translateY(-50%);
      }
    `}

  * {
    box-sizing: border-box;
  }
`;
